import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ZoomModal, ZoomNav, useDragPan, useZoomKeys } from './ZoomModal.tsx';
import { Maximize } from '../lib/icons.tsx';

/**
 * Read an asset properly: one file, big.
 *
 * The grids everywhere else are recognition aids — a tile is at most ~210px and
 * fits the whole asset into a 4:3 box so you can tell WHICH file it is. This is
 * how you tell what is IN it: a logo's edges, a photo's detail, which of two
 * near-identical headshots is the right one.
 *
 * WHY IT DOES NOT CLOSE ITS HOST. This is rendered INSIDE other modals (the
 * assets picking room, the media picker), each of which holds real state — a
 * search, a category, a tab, a set of ticked assets — and each of which has its
 * own document-level Escape handler and a backdrop that closes on mousedown.
 * Two things keep those from firing underneath us, both copied from
 * SlidePreviewModal:
 *
 *   1. The keydown listener is CAPTURE phase (`, true` below) and calls
 *      stopPropagation. Capture on `document` runs before every host's
 *      bubble-phase handler, so the event never reaches them.
 *   2. The backdrop's own mousedown calls stopPropagation before closing.
 *
 * That is why no host component needed changing to adopt this. Do not "simplify"
 * either one to a bubble-phase listener: the failure is silent and looks like
 * the whole picking room evaporating when you dismiss a picture.
 *
 * Unlike the slide zoom, the content here is a BITMAP. A slide re-lays-out at
 * any size because it is written in `cqw`, so that viewer resizes its frame.
 * An image cannot do that — past 100% it is just bigger pixels — so this offers
 * two honest states instead of a zoom range: fit the window, or actual pixels.
 * Fit NEVER upscales (a 180px headshot blown up to fill 900px is worse than
 * useless), which is also why the toggle disables itself when the asset already
 * fits.
 */

/** Bytes as something a human reads. Deck assets carry a size; bank items don't. */
function prettyBytes(n: number | null | undefined): string | null {
  if (typeof n !== 'number' || !isFinite(n) || n < 0) return null;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} kB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

/** One asset the viewer can show. The same shape is built by the deck shelf,
 *  the bank grid and the picking room, which is why it is declared here rather
 *  than in any one of them. */
export type PreviewItem = {
  key: string;
  src: string;
  kind?: 'image' | 'video';
  label?: string;
  size?: number | null;
};

export default function AssetPreviewModal({
  // The assets the arrows may walk, in the order the grid is showing them —
  // the FILTERED list, so walking never wanders outside what the search and
  // category chips say is on screen.
  items,
  index = 0,
  onClose,
}: {
  items: PreviewItem[];
  index?: number;
  onClose: () => void;
}) {
  const [idx, setIdx] = useState(index);
  // Natural size of the CURRENT asset, measured on load. null until it lands,
  // and stays null for an SVG that declares no intrinsic size (naturalWidth 0),
  // which is what hides the dimensions readout and disables the size toggle
  // rather than showing "0 × 0".
  const [natural, setNatural] = useState<{ w: number; h: number } | null>(null);
  const [actual, setActual] = useState(false);
  // The viewport the asset has to fit into. Measured rather than read off a ref
  // at render time, so "does this overflow?" survives a window resize.
  const [view, setView] = useState({ w: 0, h: 0 });
  const scrollerRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);

  useLayoutEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const measure = () => setView({ w: el.clientWidth, h: el.clientHeight });
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Follow the host if it re-points an already-open viewer at another asset.
  useEffect(() => setIdx(index), [index]);

  const item = items[idx] || items[0];
  const canNav = items.length > 1;

  const step = useCallback(
    (delta: number) => {
      setIdx((at) => Math.min(items.length - 1, Math.max(0, at + delta)));
    },
    [items.length],
  );

  // A new asset is a new measurement, and a new fit decision. Resetting `actual`
  // matters: 100% on a 4000px banner then stepping to a 180px headshot would
  // otherwise land you zoomed in on nothing.
  useEffect(() => {
    setNatural(null);
    setActual(false);
  }, [item?.key]);

  // Return focus where it came from. The magnify button that opened this is
  // inside the host modal, so restoring focus is literally "back to the panel".
  // SlidePreviewModal doesn't do this; a viewer opened from a picking room must.
  useEffect(() => {
    const from = document.activeElement as HTMLElement | null;
    closeRef.current?.focus();
    return () => {
      try {
        from?.focus?.();
      } catch {
        /* the trigger unmounted while we were open */
      }
    };
  }, []);

  useZoomKeys(canNav, step);

  // Drag to pan, for when actual size overflows the scroller. `moved` is why
  // this is shared rather than reimplemented: a pan ends in a click, and the
  // image's click toggles back to fit, so dragging to look at a corner would
  // snap you out of actual size the moment you let go.
  const { moved: dragged, handlers: pan } = useDragPan(scrollerRef, actual);

  if (!item) return null;

  const isVideo = item.kind === 'video';
  // Whether actual size would differ from fit. Unknown natural size (SVG, or
  // not loaded yet) counts as "no", so the toggle stays disabled rather than
  // promising a change it can't make.
  const overflows = !!natural && !!view.w && (natural.w > view.w || natural.h > view.h);
  const canToggle = !isVideo && overflows;
  const size = prettyBytes(item.size);

  return (
    <ZoomModal
      ariaLabel={`Preview: ${item.label}`}
      className="modal--asset-zoom"
      onClose={onClose}
      closeRef={closeRef}
      heading={item.label}
      facts={
        <span className="asset-zoom__facts muted small">
          {natural ? `${natural.w} × ${natural.h}` : null}
          {natural && size ? ' · ' : null}
          {size}
        </span>
      }
      nav={canNav ? <ZoomNav noun="asset" plural="Assets" pos={idx} total={items.length} onStep={step} /> : null}
      controls={
        <div className="zoom__controls" role="group" aria-label="Size">
          <button
            type="button"
            className="zoom__level"
            onClick={() => setActual((v) => !v)}
            disabled={!canToggle}
            title={canToggle ? 'Fit to window / actual size' : 'This asset already fits'}
            aria-pressed={actual}
          >
            {actual ? '100%' : 'Fit'}
          </button>
          <button
            type="button"
            className="iconbtn"
            onClick={() => setActual(true)}
            disabled={!canToggle || actual}
            title="Actual size"
            aria-label="Actual size"
          >
            <Maximize />
          </button>
        </div>
      }
    >
      <div
        className={'zoom__scroller' + (actual ? ' zoom__scroller--pannable' : '')}
        ref={scrollerRef}
        {...pan}
        onPointerCancel={pan.onPointerUp}
      >
        {isVideo ? (
          <video className="asset-zoom__media" src={item.src} controls loop playsInline />
        ) : (
          <img
            className={
              'asset-zoom__media' +
              (actual ? ' asset-zoom__media--actual' : '') +
              (canToggle && !actual ? ' asset-zoom__media--toggles' : '')
            }
            src={item.src}
            alt={item.label}
            onClick={() => {
              if (dragged.current) {
                dragged.current = false;
                return;
              }
              if (canToggle) setActual((v) => !v);
            }}
            onLoad={(e) => {
              const { naturalWidth: w, naturalHeight: h } = e.currentTarget;
              setNatural(w && h ? { w, h } : null);
            }}
            style={actual && natural ? { width: natural.w, height: natural.h } : undefined}
          />
        )}
      </div>

      <footer className="zoom__hint muted small">
        {canNav ? '← → for assets · ' : ''}
        {actual ? 'Drag to pan · ' : ''}
        {canToggle ? 'Click the image for actual size · ' : ''}Esc to close
      </footer>
    </ZoomModal>
  );
}
